"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Loader2, CheckCircle, AlertCircle } from "lucide-react";

interface LeadFormData {
  full_name: string;
  phone_number: string;
  brand_name: string;
  insta_handle: string;
  email: string;
}

interface FormErrors {
  full_name?: string;
  phone_number?: string;
  email?: string;
}

interface LeadFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: LeadFormData) => void;
  title?: string;
  buttonText?: string;
}

export function LeadForm({
  isOpen,
  onClose,
  onSubmit,
  title = "Get Started with PhotoZoot",
  buttonText = "Submit & Join Waitlist",
}: LeadFormProps) {
  const [formData, setFormData] = useState<LeadFormData>({
    full_name: "",
    phone_number: "",
    brand_name: "",
    insta_handle: "",
    email: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Ensure component is mounted before rendering portal
  useEffect(() => {
    setMounted(true);
  }, []);

  // Validation functions
  const validateName = (name: string): string | undefined => {
    if (!name.trim()) return "Full name is required";
    if (name.trim().length < 2) return "Name must be at least 2 characters";
    if (name.trim().length > 50) return "Name must be less than 50 characters";
    // Only allow letters, spaces, hyphens, and apostrophes
    if (!/^[a-zA-Z\s\-']+$/.test(name.trim())) {
      return "Name can only contain letters, spaces, hyphens, and apostrophes";
    }
    return undefined;
  };

  const validatePhoneNumber = (phone: string): string | undefined => {
    if (!phone.trim()) return "Phone number is required";

    // Remove all spaces and dashes for validation
    const cleanPhone = phone.replace(/[\s\-]/g, "");

    // Check if it starts with + followed by country code
    if (!cleanPhone.startsWith("+")) {
      return "Phone number must start with country code (e.g., +91, +1)";
    }

    // Check if country code is valid (1-4 digits after +)
    const countryCodeMatch = cleanPhone.match(/^\+\d{1,4}/);
    if (!countryCodeMatch) {
      return "Invalid country code format";
    }

    // Check if the remaining digits are valid (7-15 digits total including country code)
    if (cleanPhone.length < 8 || cleanPhone.length > 16) {
      return "Phone number must be 7-15 digits including country code";
    }

    // Check if all characters after + are digits
    if (!/^\+\d+$/.test(cleanPhone)) {
      return "Phone number can only contain digits after the + symbol";
    }

    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    if (!email.trim()) return "Email is required";

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return "Please enter a valid email address";
    }

    // Check email length
    if (email.trim().length > 254) {
      return "Email address is too long";
    }

    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const nameError = validateName(formData.full_name);
    if (nameError) newErrors.full_name = nameError;

    const phoneError = validatePhoneNumber(formData.phone_number);
    if (phoneError) newErrors.phone_number = phoneError;

    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof LeadFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear field-specific error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    // Clear general error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare data for cloud function
      const cloudFunctionData = {
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        brand_name: formData.brand_name.trim(),
        insta_handle: formData.insta_handle.trim(),
        phone_number: formData.phone_number.trim(),
        referral_source: "www.photozoot.in",
      };

      // Send directly to Supabase cloud function
      const response = await fetch("/api/join-waitlist", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cloudFunctionData),
      });

      if (!response.ok) {
        // Handle specific HTTP status codes
        let errorMessage = "Failed to submit form. Please try again.";

        switch (response.status) {
          case 400:
            errorMessage =
              "Invalid data provided. Please check your information and try again.";
            break;
          case 401:
            errorMessage = "Authentication failed. Please try again later.";
            break;
          case 403:
            errorMessage = "Access denied. Please try again later.";
            break;
          case 409:
            errorMessage =
              "You may have already joined our waitlist with this email or phone number. If you haven't, please contact support.";
            break;
          case 422:
            errorMessage =
              "Invalid form data. Please check your information and try again.";
            break;
          case 429:
            errorMessage =
              "Too many requests. Please wait a moment and try again.";
            break;
          case 500:
            errorMessage =
              "Server error. Please try again later or contact support.";
            break;
          case 503:
            errorMessage =
              "Service temporarily unavailable. Please try again later.";
            break;
          default:
            errorMessage = `Request failed with status ${response.status}. Please try again.`;
        }

        // Try to get more details from response body if available
        try {
          const errorData = await response.json();
          if (errorData.message || errorData.error) {
            errorMessage = errorData.message || errorData.error;
          }
        } catch {
          // If we can't parse the error response, use our default message
        }

        throw new Error(errorMessage);
      }

      await response.json();

      // Show success message
      setShowSuccess(true);

      // Call the onSubmit callback if provided
      if (onSubmit) {
        onSubmit(formData);
      }

      // Auto-close success message after 5 seconds
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        // Reset form
        setFormData({
          full_name: "",
          phone_number: "",
          brand_name: "",
          insta_handle: "",
          email: "",
        });
        setErrors({});
      }, 5000);
    } catch (error) {
      // Handle different types of errors
      if (error instanceof Error) {
        setError(error.message);
      } else if (typeof error === "string") {
        setError(error);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      onClose();
      // Reset form and error state
      setFormData({
        full_name: "",
        phone_number: "",
        brand_name: "",
        insta_handle: "",
        email: "",
      });
      setErrors({});
      setError(null);
    }
  }, [isSubmitting, onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      handleClose();
    }
  };

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, isSubmitting, handleClose]);

  // Don't render anything if not mounted or not open
  if (!mounted || !isOpen) return null;

  // Render success message
  if (showSuccess) {
    return createPortal(
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4"
        onClick={handleBackdropClick}
        role="dialog"
        aria-labelledby="success-title"
        aria-describedby="success-description"
      >
        <div className="bg-background border border-border rounded-lg shadow-2xl w-full max-w-md mx-auto p-8 text-center">
          <div className="space-y-4">
            <div className="relative">
              <CheckCircle
                className="w-16 h-16 text-green-500 mx-auto animate-pulse"
                aria-hidden="true"
              />
            </div>
            <div className="space-y-2">
              <h2
                id="success-title"
                className="text-2xl font-bold text-foreground"
              >
                Thank You!
              </h2>
              <p id="success-description" className="text-muted-foreground">
                You&apos;ve successfully joined our waitlist. We&apos;ll get
                back to you as soon as possible with exciting updates about
                PhotoZoot!
              </p>
            </div>
            <div className="pt-4">
              <p className="text-sm text-muted-foreground">
                This window will close automatically...
              </p>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Render main form
  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-labelledby="form-title"
      aria-describedby="form-description"
    >
      <div
        ref={modalRef}
        className="bg-background border border-border rounded-lg shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden relative"
      >
        {/* Header */}
        <header className="flex items-center justify-between p-6 border-b border-border bg-background sticky top-0">
          <h2 id="form-title" className="text-xl font-semibold text-foreground">
            {title}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-8 w-8 p-0 hover:bg-muted"
            disabled={isSubmitting}
            aria-label="Close form"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </header>

        {/* Form */}
        <div
          className="overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 120px)" }}
        >
          <form
            onSubmit={handleSubmit}
            className="p-6 space-y-4"
            aria-describedby="form-description"
          >
            <p id="form-description" className="sr-only">
              Fill out this form to get started with PhotoZoot
            </p>

            {/* Full Name */}
            <fieldset className="space-y-2">
              <Label htmlFor="full_name" className="text-sm font-medium">
                Full Name *
              </Label>
              <Input
                id="full_name"
                type="text"
                required
                value={formData.full_name}
                onChange={(e) => handleInputChange("full_name", e.target.value)}
                placeholder="Enter your full name"
                className={`w-full ${
                  errors.full_name ? "border-red-500 focus:border-red-500" : ""
                }`}
                disabled={isSubmitting}
                aria-required="true"
                aria-invalid={!!errors.full_name}
                aria-describedby={errors.full_name ? "name-error" : undefined}
              />
              {errors.full_name && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <span id="name-error">{errors.full_name}</span>
                </div>
              )}
            </fieldset>

            {/* Phone Number */}
            <fieldset className="space-y-2">
              <Label htmlFor="phone_number" className="text-sm font-medium">
                Contact Number *
              </Label>
              <Input
                id="phone_number"
                type="tel"
                required
                value={formData.phone_number}
                onChange={(e) =>
                  handleInputChange("phone_number", e.target.value)
                }
                placeholder="+91 98765 43210"
                className={`w-full ${
                  errors.phone_number
                    ? "border-red-500 focus:border-red-500"
                    : ""
                }`}
                disabled={isSubmitting}
                aria-required="true"
                aria-invalid={!!errors.phone_number}
                aria-describedby={
                  errors.phone_number ? "phone-error" : "phone-hint"
                }
              />
              <p id="phone-hint" className="text-xs text-muted-foreground">
                Start with country code (e.g., +91 for India, +1 for US)
              </p>
              {errors.phone_number && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <span id="phone-error">{errors.phone_number}</span>
                </div>
              )}
            </fieldset>

            {/* Email */}
            <fieldset className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Enter your email address"
                className={`w-full ${
                  errors.email ? "border-red-500 focus:border-red-500" : ""
                }`}
                disabled={isSubmitting}
                aria-required="true"
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && (
                <div className="flex items-center gap-2 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  <span id="email-error">{errors.email}</span>
                </div>
              )}
            </fieldset>

            {/* Brand Name */}
            <fieldset className="space-y-2">
              <Label htmlFor="brand_name" className="text-sm font-medium">
                Brand/Business Name *
              </Label>
              <Input
                id="brand_name"
                type="text"
                required
                value={formData.brand_name}
                onChange={(e) =>
                  handleInputChange("brand_name", e.target.value)
                }
                placeholder="Enter your brand name"
                className="w-full"
                disabled={isSubmitting}
                aria-required="true"
              />
            </fieldset>

            {/* Instagram Handle */}
            <fieldset className="space-y-2">
              <Label htmlFor="insta_handle" className="text-sm font-medium">
                Instagram Handle
              </Label>
              <Input
                id="insta_handle"
                type="text"
                value={formData.insta_handle}
                onChange={(e) =>
                  handleInputChange("insta_handle", e.target.value)
                }
                placeholder="@yourhandle (optional)"
                className="w-full"
                disabled={isSubmitting}
              />
            </fieldset>

            {/* General Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
              aria-describedby={isSubmitting ? "submitting-status" : undefined}
            >
              {isSubmitting ? (
                <>
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                  <span id="submitting-status">Submitting...</span>
                </>
              ) : (
                buttonText
              )}
            </Button>

            {/* Privacy Notice */}
            <p className="text-xs text-muted-foreground text-center">
              By submitting this form, you agree to our privacy policy and
              consent to being contacted about our services.
            </p>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
